import React from 'react';
import { Avatar } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetOrdersReportByIdQuery } from '@/store/services/reports';
import Status from '@/components/DisputeDetails.tsx/atoms/Status';
import { DetailSection } from '../SharedComponents/DetailsSection';
import UserInfo from '../SharedComponents/UserInfo';
import ReportDrawer from '../SharedComponents/ReportDrawer';
import { usePublicState } from '@/store/slices/publicSlice';

export interface RequestStatus {
  id: string;
  code: string;
  name: string;
}

export interface RequestInfo {
  status: RequestStatus;
  serial: string;
  requestBudget: number;
  requestCommission: number;
  requestReason: string;
  requestDate: string;
  orderType: string;
  orderMethod: string;
  orderId: string;
  estDelivery: string | null;
  transactionId: string;
}

export interface UserPartyInfo {
  image: string | null;
  name: string;
  email: string;
  phone: string;
  countryCode?: string;
}
export interface AdditionInfo {
  buyerType: string;
  paymentRef: string;
}

export interface OrderDetailsReportData {
  requestInfo: RequestInfo;
  sellerInfo: UserPartyInfo[];
  buyerInfo: UserPartyInfo;
  addInfo: AdditionInfo;
}

interface RequestDetailsDrawerProps {
  listOfIds: string[];
}

export const RequestDetailsDrawer: React.FC<RequestDetailsDrawerProps> = ({
  listOfIds,
}) => {
  const { modalId } = usePublicState();
  const { t } = useTranslation();

  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(modalId));

  const { data: reportDetails, isFetching } = useGetOrdersReportByIdQuery(
    modalId as string,
    { skip: !isDrawerOpen }
  );

  const response = reportDetails?.payload;

  const requestInfoItems = [
    {
      label: t('reports.deliveryStatus', 'Delivery status'),
      value: response?.requestInfo?.status ? (
        <Status text={response?.requestInfo?.status?.name} variant="info" />
      ) : (
        '-'
      ),
    },
    {
      label: t('reports.transactionId', 'Transaction ID'),
      value: response?.requestInfo?.transactionId || '-',
    },
    {
      label: t('reports.orderBudget', 'Order budget'),
      value: response?.requestInfo?.requestBudget || '-',
    },
    {
      label: t('reports.requestCommission', 'Request commission'),
      value: response?.requestInfo?.requestCommission || '-',
    },
    {
      label: t('reports.requestDate', 'Request date'),
      value: response?.requestInfo?.requestDate
        ? dayjs(response?.requestInfo?.requestDate).isValid()
          ? dayjs(response?.requestInfo?.requestDate).format('DD MMM YYYY')
          : '-'
        : '-',
    },
    {
      label: t('reports.orderId', 'Order ID'),
      value: response?.requestInfo?.serial || '-',
    },
    {
      label: t('reports.estDelivery', 'Est. Delivery'),
      value: response?.requestInfo?.estDelivery || '-',
    },
    {
      label: t('reports.orderType', 'Order type'),
      value: response?.requestInfo?.orderType || '-',
    },
    {
      label: t('reports.orderMethod', 'Order method'),
      value: response?.requestInfo?.orderMethod || '-',
    },
  ];

  const additionalInfoItems = [
    {
      label: t('reports.paymentReference', 'Payment reference'),
      value: response?.addInfo?.paymentRef || '-',
    },
    {
      label: t('reports.buyerType', 'Buyer type'),
      value: response?.addInfo?.buyerType || '-',
    },
  ];

  const sellers = Array.isArray(response?.sellerInfo)
    ? response?.sellerInfo
    : response?.sellerInfo
      ? [response.sellerInfo]
      : [];

  return (
    <ReportDrawer
      title={t('reports.orderDetails', 'Order details')}
      listOfIds={listOfIds}
    >
      <div className="space-y-6">
        {/* Request Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.requestInformation', 'Request information')}
          items={requestInfoItems}
        />

        {/* Additional Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.additionalInformation', 'Additional information')}
          items={additionalInfoItems}
        />

        {/* Seller Information Section */}
        {sellers.map((s, idx) => (
          <UserInfo
            key={idx}
            loading={isFetching}
            sectionTitle={t('reports.sellerInformation', 'Seller information')}
            name={s?.name || ''}
            email={s?.email || ''}
            phone={s?.phone || ''}
            image={s?.image || ''}
            countryCode={s?.countryCode || ''}
          />
        ))}

        {/* Buyer Information Section */}
        <UserInfo
          loading={isFetching}
          sectionTitle={t('reports.buyerInformation', 'Buyer information')}
          name={response?.buyerInfo?.name || ''}
          email={response?.buyerInfo?.email || ''}
          phone={response?.buyerInfo?.phone || ''}
          image={response?.buyerInfo?.image || ''}
          countryCode={response?.buyerInfo?.countryCode || ''}
        />
      </div>
    </ReportDrawer>
  );
};
